import { useEditorContext } from '@editor/contexts/Editor'
import { CameraProfile, FrontCameraProfile } from '@editor/interfaces/camera'
import { Editor, PointArray } from '@editor/interfaces/interfaces'
import { fitCameraDistance } from '@editor/services/Camera'
import { findCenter } from '@editor/services/Points'
import { useAppSelector } from '@editor/store/app'
import { useLocation } from 'react-router'
import { Matrix4, Plane, Quaternion, Vector3 } from 'three'
import { ArcballControls as ThreeArcballControls } from 'three/addons/controls/ArcballControls.js'

interface FocusItemOptions {
  /** Whether to skip using camera profile for determining camera direction. Defaults to false. */
  skipProfile?: boolean
  /** Optional fixed distance from target to place camera. If not provided, distance will be calculated to fit all points in view. */
  distance?: number
  /** Optional custom camera profile to determine camera direction. If not provided, area camera profile or current camera will be used. */
  customProfile?: FrontCameraProfile
}

/**
 * Similarity threshold for determining if the calculated plane of provided points
 * is nearly parallel to camera view direction.
 *
 * angleInRadians = Math.acos(threshold)
 */
const SIMILARITY_THRESHOLD = 0.9 // ~25 degrees tolerance

export const useFocusItem = (editorContext?: Editor) => {
  const location = useLocation()
  const urlParams = new URLSearchParams(location.search)
  const shouldAnimate = urlParams.get('ca') !== 'false'

  // Context
  const { cameraRef, arcballControlsRef } = { ...useEditorContext(), ...editorContext }

  // Store
  const cameraProfile = useAppSelector((state) => state.toolCameraProfile.cameraProfile)

  return async (input: PointArray[], { customProfile, skipProfile = false, distance }: FocusItemOptions = {}) => {
    if (!cameraRef.current || !(arcballControlsRef.current instanceof ThreeArcballControls) || input.length < 2)
      return false

    // If there's a custom profile, use that directly instead of calculating everything.
    if (customProfile) {
      await arcballControlsRef.current.animateToProfile(
        customProfile as CameraProfile,
        undefined,
        shouldAnimate ? undefined : 0,
      )
      return true
    }

    // If there are only 2 points, we will add using midpoint as an additional point.
    // This generally only applied for polylines as there's no way a plane can be formed with only 2 points.
    const positions: Vector3[] = []
    if (input.length === 2) {
      const midpoint = new Vector3(
        (input[0][0] + input[1][0]) / 2,
        (input[0][1] + input[1][1]) / 2,
        (input[0][2] + input[1][2]) / 2,
      )
      positions.push(new Vector3(...input[0]), midpoint, new Vector3(...input[1]))
    } else {
      input.forEach((point) => positions.push(new Vector3(...point)))
    }

    // Calculate target as the center of the plane
    const target = findCenter(positions)
    if (!target) throw new Error('Failed to calculate center of the plane')

    // Create the plane
    const plane = new Plane().setFromCoplanarPoints(positions[0], positions[1], positions[2])

    // Determine which side of the plane to place the camera
    // based on camera profile or current camera position
    const cameraUp = new Vector3()
    const cameraForward = new Vector3(0, 0, -1)
    const referencePosition = new Vector3()
    if (cameraProfile && !skipProfile) {
      const {
        state: {
          arcballState: { cameraUp: up },
        },
        target: cameraTarget,
        positions: { front },
      } = cameraProfile

      referencePosition.fromArray(front)
      cameraUp.set(up.x, up.y, up.z)
      cameraForward.fromArray(cameraTarget).sub(referencePosition).normalize()
    } else {
      cameraUp.setFromMatrixColumn(cameraRef.current.matrix, 1)
      cameraForward.applyQuaternion(cameraRef.current.quaternion).normalize()
      referencePosition.copy(cameraRef.current.position)
    }

    // Check if plane is nearly parallel to camera's view direction
    // If so, use camera's direction for more stable focusing
    const targetNormal = new Vector3()
    const planeNormal = plane.normal.clone().normalize()
    const similarity = Math.abs(planeNormal.dot(cameraForward))

    // Plane is nearly parallel to camera view, use camera direction as normal
    // cameraForward points FROM camera TO scene, so negate it to point FROM plane TO camera
    // Only do this for camera profile as it provides a more stable reference direction.
    // For polyline with only 2 points, we will always use camera direction as normal since
    // the calculated plane is not useful anyway.
    if ((similarity > SIMILARITY_THRESHOLD && cameraProfile && !skipProfile) || input.length === 2) {
      targetNormal.copy(cameraForward.clone().negate())
    } else {
      // Determine which side of the plane the reference position is on
      // If distance >= 0, camera is on the side the normal points to
      const distanceToPlane = plane.distanceToPoint(referencePosition)
      targetNormal.copy(distanceToPlane >= 0 ? plane.normal.clone() : plane.normal.clone().negate())
    }

    // Calculate new parameters for the camera to focus on the target
    const cameraDistance =
      distance ??
      fitCameraDistance(positions, targetNormal, cameraUp, cameraRef.current.fov, cameraRef.current.aspect, 3)
    const newCameraPosition = new Vector3().copy(target).add(targetNormal.multiplyScalar(cameraDistance))
    const newCameraQuaternion = new Quaternion().setFromRotationMatrix(
      new Matrix4().lookAt(newCameraPosition, target, cameraUp),
    )

    // Update ArcballControls target to the new target
    if (shouldAnimate) {
      await arcballControlsRef.current.animateToTarget(target, newCameraPosition, newCameraQuaternion)
    } else {
      arcballControlsRef.current.focusToTarget(target, newCameraPosition, newCameraQuaternion)
    }

    return true
  }
}
